/**
 * Cenário do PCP (plano 4.5; não grava nada no backend): "e se eu mover
 * este cliente para outra data?". Reprojeta a fila com a nova data, mostra
 * quem seria empurrado e diz EXATAMENTE o que digitar na planilha, porque a
 * fonte da verdade continua sendo ela.
 *
 * O "o que digitar" usa o calendário DA PLANILHA (feriados da aba TABELAS +
 * fim de semana), não o da unidade: é o que o Excel usa no WORKDAY de
 * CO = WORKDAY(APROVAÇÃO, PRAZO) e CR = WORKDAY(CO, CQ). Com o calendário
 * da unidade o número de CQ sairia "certo" e o Excel calcularia outra data.
 *
 * Depois de digitar, o PCP pode "marcar para conferir": a marca fica neste
 * aparelho e, na leitura seguinte da planilha, a tela diz se foi aplicado.
 */
import {
  contarDiasUteis,
  diasCorridosEntre,
  duEntre,
  ehDiaUtil,
  ehIsoValido,
  somarDiasCorridos,
  somarDu,
} from "./calendario";
import { CAMADAS_DA_FILA } from "./classificacao";
import { chaveDoItem, clientesEmpurrados, limiteDaMarcenaria, ocupacaoAntesDepois, periodosAte } from "./comparacao";
import { projetarFila } from "./fila";
import { ESCALA } from "./periodos";
import { anosSemCalendario } from "./ritmo";
import { INDISPONIVEL } from "./simulacao";

/** Campo da planilha que o PCP digita (e que a conferência relê). */
export const CAMPO = Object.freeze({
  ENTREGA_CONTRATO: "entrega_contrato",
  DIAS_REPROGRAMADO: "dias_reprogramado",
  DATA_REPROGRAMADA: "data_reprogramada",
});

export const TIPO_INSTRUCAO = Object.freeze({
  CONTRATO: "contrato",
  DIAS_REPROGRAMADO: "dias_reprogramado",
  DATA_REPROGRAMADA: "data_reprogramada",
  FORMULA_VAZIA: "formula_vazia",
});

export const STATUS_CONFERENCIA = Object.freeze({
  APLICADO: "aplicado",
  DIVERGIU: "divergiu",
  PENDENTE: "pendente",
});

/** Marca mais velha que isso sai sozinha (a reprogramação já virou outra). */
export const DIAS_VALIDADE_MARCA = 30;

export const MOTIVO_CENARIO = Object.freeze({
  ...INDISPONIVEL,
  PEDIDO_SEM_ABERTOS: "pedido_sem_abertos",
});

function dataBr(iso) {
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
}

function ddmm(iso) {
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
}

/** WORKDAY(inicio, dias) do Excel no calendário da planilha. */
export function workdayDaPlanilha(inicio, dias, naoUteisPlanilha) {
  return somarDu(inicio, dias, naoUteisPlanilha);
}

/**
 * O `n` tal que WORKDAY(de, n) = `ate` (que precisa ser dia útil). Para
 * frente é a contagem de du em (de, ate]; para trás, de du em [ate, de).
 * As duas formas valem mesmo quando `de` não é dia útil.
 */
export function diasParaChegar(de, ate, naoUteisPlanilha) {
  if (ate >= de) return duEntre(de, ate, naoUteisPlanilha);
  return -contarDiasUteis(ate, somarDiasCorridos(de, -1), naoUteisPlanilha);
}

const MAXIMO_DATAS_NO_AVISO = 3;

/**
 * Dias sem expediente da UNIDADE (em dia de semana) entre CO e a nova data
 * que a aba TABELAS não tem: o Excel conta esses dias como úteis. O CQ já
 * sai certo para o Excel (é contado no calendário dele); o aviso explica por
 * que o número parece "um a mais" para quem conta no calendário da fábrica.
 */
function feriadosQueFaltamNaPlanilha(de, ate, naoUteisPlanilha, naoUteisUnidade) {
  const [inicio, fim] = de <= ate ? [de, ate] : [ate, de];
  return [...(naoUteisUnidade || [])]
    .filter((dia) => dia > inicio && dia <= fim && ehDiaUtil(dia) && !naoUteisPlanilha.has(dia))
    .sort();
}

function avisoDeFeriadosQueFaltam(dias) {
  if (dias.length === 0) return null;
  const lista = dias.slice(0, MAXIMO_DATAS_NO_AVISO).map(ddmm).join(", ");
  const resto = dias.length > MAXIMO_DATAS_NO_AVISO ? ` e mais ${dias.length - MAXIMO_DATAS_NO_AVISO}` : "";
  return (
    `a aba TABELAS não tem ${lista}${resto} como feriado: o Excel conta como dia útil ` +
    "(o CQ acima já considera isso)"
  );
}

function avisoDaUnidade(data, naoUteisUnidade) {
  if (ehDiaUtil(data, naoUteisUnidade)) return null;
  return `${dataBr(data)} é dia sem expediente no calendário da unidade`;
}

function instrucaoSemAprovacao(linha, novaData) {
  return {
    tipo: TIPO_INSTRUCAO.CONTRATO,
    campo: CAMPO.ENTREGA_CONTRATO,
    coluna: "N",
    esperado: novaData,
    anterior: linha.entrega_contrato ?? null,
    dataFinal: novaData,
    texto: `digite ENTREGA PREVISTA (N) = ${dataBr(novaData)}`,
    avisos: [],
  };
}

function crDigitadoPorCima(linha, naoUteisPlanilha) {
  if (!linha.data_reprogramada) return false;
  const pelaFormula = workdayDaPlanilha(linha.entrega_cliente, linha.dias_reprogramado ?? 0, naoUteisPlanilha);
  return linha.data_reprogramada !== pelaFormula;
}

function instrucaoDeCq(linha, novaData, naoUteisPlanilha, naoUteisUnidade) {
  const alvo = ehDiaUtil(novaData, naoUteisPlanilha) ? novaData : somarDu(novaData, 1, naoUteisPlanilha);
  const cq = diasParaChegar(linha.entrega_cliente, alvo, naoUteisPlanilha);
  const avisos = [];
  if (alvo !== novaData) avisos.push(`o Excel só aceita dia útil: CR ficará ${ddmm(alvo)}`);
  const faltam = avisoDeFeriadosQueFaltam(
    feriadosQueFaltamNaPlanilha(linha.entrega_cliente, alvo, naoUteisPlanilha, naoUteisUnidade),
  );
  if (faltam) avisos.push(faltam);
  return {
    tipo: TIPO_INSTRUCAO.DIAS_REPROGRAMADO,
    campo: CAMPO.DIAS_REPROGRAMADO,
    coluna: "CQ",
    esperado: cq,
    anterior: linha.dias_reprogramado ?? null,
    dataFinal: alvo,
    texto: `digite DIAS REPROGRAMADO (CQ) = ${cq} · DATA REPROGRAMADA (CR) deve ficar ${dataBr(alvo)}`,
    avisos,
  };
}

/**
 * O que digitar na planilha para o ambiente `linha` passar a ter entrega em
 * `novaData`:
 * - sem aprovação: N (a data do contrato ainda é a régua);
 * - aprovado sem CO: a fórmula de CO está vazia, nada a calcular;
 * - aprovado com CR digitado por cima da fórmula: digitar CR de novo;
 * - aprovado: CQ = du de CO até a nova data, no calendário da planilha.
 * Retorna `{tipo, campo, coluna, esperado, anterior, dataFinal, texto, avisos}`
 * (`campo` null quando não há o que digitar).
 */
export function instrucaoDeReprogramacao({ linha, novaData, feriadosPlanilha = [], naoUteisUnidade } = {}) {
  if (!ehIsoValido(novaData)) throw new TypeError(`Nova data inválida: ${String(novaData)}.`);
  const naoUteisPlanilha = new Set(feriadosPlanilha);
  let instrucao;
  if (!linha.aprovacao) {
    instrucao = instrucaoSemAprovacao(linha, novaData);
  } else if (!linha.entrega_cliente) {
    instrucao = {
      tipo: TIPO_INSTRUCAO.FORMULA_VAZIA,
      campo: null,
      coluna: "CO",
      esperado: null,
      anterior: null,
      dataFinal: null,
      texto: `fórmula de ENTREGA CLIENTE (CO) vazia: confira a linha ${linha.linha_planilha}`,
      avisos: [],
    };
  } else if (crDigitadoPorCima(linha, naoUteisPlanilha)) {
    instrucao = {
      tipo: TIPO_INSTRUCAO.DATA_REPROGRAMADA,
      campo: CAMPO.DATA_REPROGRAMADA,
      coluna: "CR",
      esperado: novaData,
      anterior: linha.data_reprogramada,
      dataFinal: novaData,
      texto: `digite DATA REPROGRAMADA (CR) = ${dataBr(novaData)}`,
      avisos: ["CR foi digitada por cima da fórmula nesta linha"],
    };
  } else {
    instrucao = instrucaoDeCq(linha, novaData, naoUteisPlanilha, naoUteisUnidade);
  }
  const avisoUnidade = instrucao.dataFinal ? avisoDaUnidade(instrucao.dataFinal, naoUteisUnidade) : null;
  return avisoUnidade ? { ...instrucao, avisos: [...instrucao.avisos, avisoUnidade] } : instrucao;
}

function doPedido(item, pedido, cliente) {
  return CAMADAS_DA_FILA.includes(item.camada) && item.pedido === pedido && item.cliente === cliente;
}

/**
 * `cenarioDeReprogramacao({painel, pedido, cliente, novaData, G, feriadosPlanilha,
 * anosComRegistro?, escala?})`: todos os ambientes abertos (da fila) do
 * pedido recebem `novaData`; a fila é reprojetada.
 *
 * Retorna `{indisponivel:true, motivo}` ou `{indisponivel:false, itens, empurrados,
 * ocupacao, pedidoDepois:{primeira, ultima, termino, podeAtrasar}, projecao}`,
 * com `itens = [{...item, antes:{posicao, dia}, depois:{posicao, dia}, instrucao}]`.
 */
export function cenarioDeReprogramacao({
  painel,
  pedido,
  cliente,
  novaData,
  G = 0,
  feriadosPlanilha = [],
  anosComRegistro,
  escala = ESCALA.MES,
} = {}) {
  const antes = painel?.fila;
  // O ritmo e o peso da própria projeção: na leitura com pesos (exceções
  // marcadas como régua oficial) o ritmo é medido nos mesmos pesos.
  const ritmo = painel?.ritmo?.ritmo ? antes?.ritmo : null;
  if (!ritmo || !antes) return { indisponivel: true, motivo: painel?.ritmo?.motivo || MOTIVO_CENARIO.SEM_RITMO };
  if (!ehIsoValido(novaData)) throw new TypeError(`Nova data inválida: ${String(novaData)}.`);
  const { hoje, naoUteis } = antes;
  const movidos = (painel.abertos || []).filter((item) => doPedido(item, pedido, cliente));
  if (movidos.length === 0) return { indisponivel: true, motivo: MOTIVO_CENARIO.PEDIDO_SEM_ABERTOS };

  const chavesMovidas = new Set(movidos.map(chaveDoItem));
  const projecao = projetarFila({
    abertos: (painel.abertos || []).map((item) =>
      chavesMovidas.has(chaveDoItem(item)) ? { ...item, entrega: novaData } : item,
    ),
    ritmo,
    naoUteis,
    hoje,
    precisao: painel.precisao || antes.precisao,
    peso: antes.peso,
  });
  const porChave = (lista) => new Map(lista.map((item) => [chaveDoItem(item), item]));
  const antesPorChave = porChave(antes.itens);
  const depoisPorChave = porChave(projecao.itens);
  const itens = movidos.map((item) => {
    const a = antesPorChave.get(chaveDoItem(item));
    const d = depoisPorChave.get(chaveDoItem(item));
    return {
      ...item,
      antes: { posicao: a?.posicao ?? null, dia: a?.diaProjetado ?? null },
      depois: { posicao: d?.posicao ?? null, dia: d?.diaProjetado ?? null },
      instrucao: instrucaoDeReprogramacao({ linha: item, novaData, feriadosPlanilha, naoUteisUnidade: naoUteis }),
    };
  });
  const posicoes = itens.map((i) => i.depois.posicao).filter(Boolean);
  const termino = itens.map((i) => i.depois.dia).filter(Boolean).sort().pop() || null;
  const limite = limiteDaMarcenaria(novaData, G, naoUteis);
  const ate = termino && termino > novaData ? termino : novaData;
  return {
    indisponivel: false,
    itens,
    empurrados: clientesEmpurrados({ antes, depois: projecao, G, ignorar: chavesMovidas }),
    ocupacao: ocupacaoAntesDepois({ antes, depois: projecao, periodos: periodosAte(escala, hoje, ate) }),
    pedidoDepois: {
      primeira: posicoes.length ? Math.min(...posicoes) : null,
      ultima: posicoes.length ? Math.max(...posicoes) : null,
      termino,
      limite,
      podeAtrasar: Boolean(termino && termino > limite),
    },
    calendarioAusente: anosComRegistro
      ? anosSemCalendario(novaData < hoje ? novaData : hoje, novaData < hoje ? hoje : novaData, anosComRegistro)
      : [],
    projecao,
  };
}

// --------------------------------------------------------- marcar para conferir

/**
 * Marca guardada neste aparelho: `{chave, campo, esperado, criadoEm}` (o
 * contrato) + `anterior` (valor na hora de marcar, para distinguir
 * "ainda não digitou" de "digitou outra coisa") e `rotulo` (para mostrar
 * mesmo se a linha sumir da planilha).
 */
export function novaMarca({ item, instrucao, hoje }) {
  if (!instrucao?.campo || !item?.chave) return null;
  return {
    chave: item.chave,
    campo: instrucao.campo,
    esperado: instrucao.esperado,
    anterior: instrucao.anterior ?? null,
    criadoEm: hoje,
    rotulo: [item.pedido, item.cliente, item.ambiente].filter(Boolean).join(" · "),
  };
}

/** Junta marcas novas às guardadas; a mesma chave + campo é substituída. */
export function juntarMarcas(guardadas, novas) {
  const porId = new Map((guardadas || []).map((m) => [`${m.chave}|${m.campo}`, m]));
  (novas || []).filter(Boolean).forEach((m) => porId.set(`${m.chave}|${m.campo}`, m));
  return [...porId.values()];
}

function diferencaEmDu(marca, atual, naoUteisPlanilha) {
  if (marca.campo === CAMPO.DIAS_REPROGRAMADO) return atual - marca.esperado;
  return duEntre(marca.esperado, atual, naoUteisPlanilha);
}

function statusDaMarca(marca, linha, naoUteisPlanilha) {
  const atual = linha ? linha[marca.campo] ?? null : null;
  if (linha && atual === marca.esperado) return { status: STATUS_CONFERENCIA.APLICADO, atual, diferenca: 0 };
  if (!linha || atual === null || atual === (marca.anterior ?? null)) {
    return { status: STATUS_CONFERENCIA.PENDENTE, atual, diferenca: null };
  }
  return { status: STATUS_CONFERENCIA.DIVERGIU, atual, diferenca: diferencaEmDu(marca, atual, naoUteisPlanilha) };
}

/**
 * Confere as marcas contra a planilha lida agora (`linhas` = linhas do
 * extrato, a última por chave vale). Retorna:
 * - `itens`: `[{...marca, status, atual, diferenca}]` para mostrar;
 * - `guardar`: o que continua no aparelho. Marca com mais de 30 dias sai;
 *   marca aplicada fica até o fim do dia em que foi vista aplicada (para o
 *   PCP ver o "aplicado") e sai na leitura do dia seguinte.
 */
export function conferirMarcas({ marcas, linhas, hoje, feriadosPlanilha = [] } = {}) {
  const naoUteisPlanilha = new Set(feriadosPlanilha);
  const porChave = new Map();
  (linhas || []).forEach((linha) => {
    if (linha.chave) porChave.set(linha.chave, linha);
  });
  const itens = [];
  const guardar = [];
  (marcas || []).forEach((marca) => {
    if (!marca?.chave || !marca.campo || !ehIsoValido(marca.criadoEm)) return;
    if (diasCorridosEntre(marca.criadoEm, hoje) > DIAS_VALIDADE_MARCA) return;
    if (marca.aplicadoEm && marca.aplicadoEm < hoje) return;
    const resultado = statusDaMarca(marca, porChave.get(marca.chave), naoUteisPlanilha);
    // Voltou a não bater (alguém desfez na planilha): deixa de ser aplicada.
    const { aplicadoEm, ...semAplicacao } = marca;
    const guardada =
      resultado.status === STATUS_CONFERENCIA.APLICADO ? { ...semAplicacao, aplicadoEm: aplicadoEm || hoje } : semAplicacao;
    guardar.push(guardada);
    itens.push({ ...guardada, ...resultado });
  });
  return { itens, guardar };
}
